import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable } from 'mobx'
import * as overclaimAPI from '@App/api/routes/overclaim'
import { WorkModel } from '@Entities/work/work'
import { bulkAmendOverclaimShares } from '@App/api/routes/overclaim'
import { GroupedOverclaims } from '@Scenes/RightsholderScene/RightsholderOverclaim/RightsholderOverclaimSearch/components/BulkAmendmentTable'
import { BulkActionStore } from '@App/stores/search/bulk-action-store'

export enum OverclaimBulkActionType {
    Relinquishment = 'RELINQUISHMENT',
    DocumentUpload = 'DOCUMENT_UPLOAD',
    AmendmentSelection = 'AMENDMENT_SELECTION',
    AmendmentEdit = 'AMENDMENT_EDIT',
}

export const bulkActionLabels: Record<OverclaimBulkActionType, string> = {
    [OverclaimBulkActionType.Relinquishment]: 'relinquishment',
    [OverclaimBulkActionType.DocumentUpload]: 'document upload',
    [OverclaimBulkActionType.AmendmentSelection]: 'share amendment',
    [OverclaimBulkActionType.AmendmentEdit]: 'share amendment',
}

export default class OverclaimSearchBulkActionStore extends BulkActionStore<OverclaimBulkActionType> {
    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        super()
        this.rootStore = rootStore
        makeObservable<
            OverclaimSearchBulkActionStore,
            '_isAmendmentTableOpen' | '_isAmendmentInReview' | '_amendmentWorklist' | '_groupedOverclaimsForAmendment'
        >(this, {
            _isAmendmentTableOpen: observable,
            _isAmendmentInReview: observable,
            _amendmentWorklist: observable,
            _groupedOverclaimsForAmendment: observable,
            setIsAmendmentTableOpen: action,
            setIsAmendmentInReview: action,
            setAmendmentWorkList: action,
            setGroupedOverclaimsForAmendment: action,
            resetAmendmentTable: action,
        })
    }

    private _isAmendmentTableOpen = false

    private _isAmendmentInReview = false

    private _amendmentWorklist: Map<number, WorkModel> = new Map()

    private _groupedOverclaimsForAmendment: Map<string, GroupedOverclaims> = new Map()

    get isAmendmentTableOpen(): boolean {
        return this._isAmendmentTableOpen
    }

    get isAmendmentInReview(): boolean {
        return this._isAmendmentInReview
    }

    get amendmentWorkList(): Map<number, WorkModel> {
        return this._amendmentWorklist
    }

    get groupedOverclaimsForAmendment(): Map<string, GroupedOverclaims> {
        return this._groupedOverclaimsForAmendment
    }

    public setIsAmendmentTableOpen(isAmendmentTableOpen: boolean): void {
        this._isAmendmentTableOpen = isAmendmentTableOpen
    }

    public setIsAmendmentInReview(inReview: boolean): void {
        this._isAmendmentInReview = inReview
    }

    public setAmendmentWorkList(workList: Map<number, WorkModel>): void {
        this._amendmentWorklist = workList
    }

    public setGroupedOverclaimsForAmendment(grouped: Map<string, GroupedOverclaims>): void {
        this._groupedOverclaimsForAmendment = grouped
    }

    public submitBulkRelinquishment = async () =>
        overclaimAPI.createBulkOverclaimRelinquishment(
            this.rootStore.rightsholderStore.currentRightsholderId,
            Array.from(this._idsSelectedForBulkAction)
        )

    async submitBulkAmendOverclaim(submissions: WorkModel[]): Promise<void> {
        try {
            await bulkAmendOverclaimShares(
                Array.from(this._idsSelectedForBulkAction),
                this.rootStore.rightsholderStore.currentRightsholderId,
                submissions
            )
        } catch {
            this.rootStore.popupStore.setAlertData(
                `Your amendments are unable to be submitted at this time.
                 Please reach out to our Support Team via the Contact Us page.`,
                true
            )
        }
    }

    resetStore(): void {
        this.setActiveBulkAction(null)
        this._isAmendmentTableOpen = false
    }

    resetAmendmentTable(): void {
        this._isAmendmentTableOpen = false
        this._isAmendmentInReview = false
        this._amendmentWorklist = new Map<number, WorkModel>()
        this._groupedOverclaimsForAmendment = new Map<string, GroupedOverclaims>()
    }
}
